/**
 * Risk Scoring API Service
 * Handles communication with backend for risk scoring functionality
 */

import { apiClient as api } from '../api';
import { LikelihoodFactors, ImpactFactors, CalculationResult, ImpactCalculationResult } from './RiskScoreCalculator';

const API_BASE_URL = '/api/comprehensive-risk-assessment';

// Request/Response Types
export interface SaveRiskScoringRequest {
  likelihood: LikelihoodFactors;
  impact: ImpactFactors;
  calculatedScores: {
    likelihood: number;
    impact: number;
    confidence: number;
    reasoning: string;
    impactReasoning?: string;
  };
}

export interface RiskScoringData {
  likelihood: LikelihoodFactors;
  impact: ImpactFactors;
  calculatedScores: {
    likelihood: number;
    impact: number;
    confidence: number;
    reasoning: string;
    impactReasoning?: string;
    finalRiskScore: number;
  };
  metadata: {
    scoringLastUpdated: string;
    scoredBy: string;
  };
  original: {
    inherentProbability: number;
    inherentImpact: number;
  };
}

export interface ScoringConfiguration {
  weights?: {
    volume: number;
    automation: number;
    historical: number;
    complexity: number;
    controls: number;
  };
  volumeThresholds?: { [key: string]: number };
  automationScoring?: {
    manual: number;
    'semi-automated': number;
    'fully-automated': number;
  };
  historicalThresholds?: {
    per_month: { [key: string]: number };
  };
}

// Note: Auth headers are now handled automatically by the api client

/**
 * Save risk scoring data
 */
export const saveRiskScoring = async (
  projectId: number,
  riskId: string,
  data: SaveRiskScoringRequest
): Promise<{ success: boolean; message: string; data: any }> => {
  const url = `${API_BASE_URL}/${projectId}/risks/${riskId}/scoring`;

  console.log('🌐 [riskScoringAPI] saveRiskScoring() called');
  console.log('   ├─ Method: PUT');
  console.log('   ├─ URL:', url);
  console.log('   ├─ Project ID:', projectId);
  console.log('   ├─ Risk ID:', riskId);
  console.log('   └─ Payload:', JSON.stringify(data, null, 2));

  try {
    console.log('   📤 Sending PUT request...');
    const response = await api.put(url, data);

    console.log('✅ [riskScoringAPI] Save successful!');
    console.log('   ├─ Status:', response.status);
    console.log('   ├─ Status Text:', response.statusText);
    console.log('   └─ Response Data:', JSON.stringify(response.data, null, 2));

    return response.data;
  } catch (error: any) {
    console.error('❌ [riskScoringAPI] Save failed!');
    console.error('   ├─ Error message:', error.message);
    console.error('   ├─ Response status:', error.response?.status);
    console.error('   ├─ Response data:', error.response?.data);
    console.error('   └─ Full error:', error);

    throw new Error(
      error.response?.data?.error ||
      error.message ||
      'Failed to save risk scoring'
    );
  }
};

/**
 * Get risk scoring data for a specific risk
 */
export const getRiskScoring = async (
  projectId: number,
  riskId: string
): Promise<RiskScoringData | null> => {
  const url = `${API_BASE_URL}/${projectId}/risks/${riskId}/scoring`;

  console.log('🌐 [riskScoringAPI] getRiskScoring() called');
  console.log('   ├─ Method: GET');
  console.log('   ├─ URL:', url);
  console.log('   ├─ Project ID:', projectId);
  console.log('   └─ Risk ID:', riskId);

  try {
    console.log('   📥 Sending GET request...');
    const response = await api.get(url);

    console.log('✅ [riskScoringAPI] Get successful!');
    console.log('   ├─ Status:', response.status);
    console.log('   ├─ Response success:', response.data.success);

    if (response.data.success) {
      console.log('   ├─ Data found: YES');
      console.log('   └─ Response Data:', JSON.stringify(response.data.data, null, 2));
      return response.data.data;
    }

    console.log('   └─ Data found: NO (success=false)');
    return null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No scoring data exists yet - this is okay
      console.log('📝 [riskScoringAPI] No scoring data found for risk:', riskId);
      console.log('   └─ This is normal for first-time scoring');
      return null;
    }

    console.error('❌ [riskScoringAPI] Get failed!');
    console.error('   ├─ Error message:', error.message);
    console.error('   ├─ Response status:', error.response?.status);
    console.error('   ├─ Response data:', error.response?.data);
    console.error('   └─ Full error:', error);

    throw new Error(
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch risk scoring'
    );
  }
};

/**
 * Get risk scoring configuration
 */
export const getScoringConfiguration = async (
  projectId?: number
): Promise<ScoringConfiguration> => {
  try {
    const url = projectId
      ? `${API_BASE_URL}/scoring/configuration/${projectId}`
      : `${API_BASE_URL}/scoring/configuration`;

    const response = await api.get(url);

    if (response.data.success) {
      return response.data.data;
    }

    // Return default configuration if not found
    return {
      weights: {
        volume: 0.25,
        automation: 0.20,
        historical: 0.30,
        complexity: 0.15,
        controls: 0.10
      },
      volumeThresholds: {
        '1': 0,
        '2': 100,
        '3': 1000,
        '4': 10000,
        '5': 100000
      },
      automationScoring: {
        'manual': 5,
        'semi-automated': 3,
        'fully-automated': 1
      },
      historicalThresholds: {
        per_month: {
          '1': 0,
          '2': 0.1,
          '3': 0.5,
          '4': 1.0,
          '5': 2.0
        }
      }
    };
  } catch (error: any) {
    console.error('Error fetching scoring configuration:', error);

    // Return default configuration on error
    return {
      weights: {
        volume: 0.25,
        automation: 0.20,
        historical: 0.30,
        complexity: 0.15,
        controls: 0.10
      },
      volumeThresholds: {
        '1': 0,
        '2': 100,
        '3': 1000,
        '4': 10000,
        '5': 100000
      },
      automationScoring: {
        'manual': 5,
        'semi-automated': 3,
        'fully-automated': 1
      },
      historicalThresholds: {
        per_month: {
          '1': 0,
          '2': 0.1,
          '3': 0.5,
          '4': 1.0,
          '5': 2.0
        }
      }
    };
  }
};

/**
 * Check if risk has scoring data
 */
export const hasRiskScoring = async (
  projectId: number,
  riskId: string
): Promise<boolean> => {
  try {
    const data = await getRiskScoring(projectId, riskId);
    return data !== null && data.calculatedScores?.likelihood !== null;
  } catch (error) {
    return false;
  }
};

export const riskScoringAPI = {
  saveRiskScoring,
  getRiskScoring,
  getScoringConfiguration,
  hasRiskScoring
};
